import React, { useRef, useEffect } from 'react'
import { init, getInstanceByDom, use, EChartsType } from 'echarts/core'
import { FunnelChart } from 'echarts/charts'
import type { EChartsOption, SetOptionOpts } from 'echarts'
import {
  LegendComponent,
  GridComponent,
  TooltipComponent,
  ToolboxComponent,
  TitleComponent,
  DataZoomComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

export interface ReactEChartsProps {
  option: EChartsOption
  settings?: SetOptionOpts
  loading?: boolean
  theme?: 'light' | 'dark'
}

use([
  LegendComponent,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  ToolboxComponent, // A group of utility tools, which includes export, data view, dynamic type switching, data area zooming, and reset.
  DataZoomComponent,
  CanvasRenderer,
  FunnelChart,
])

export function ReactECharts({
  option,
  settings,
  loading,
  theme,
}: ReactEChartsProps): JSX.Element {
  const chartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Initialize chart
    let chart: EChartsType | undefined
    if (chartRef.current !== null) chart = init(chartRef.current, theme)

    // Add chart resize listener
    // ResizeObserver is leading to a bit janky UX
    function resizeChart(): void {
      chart?.resize()
    }
    window.addEventListener('resize', resizeChart)

    // Return cleanup function
    return () => {
      chart?.dispose()
      window.removeEventListener('resize', resizeChart)
    }
  }, [theme])

  useEffect(() => {
    // Update chart
    if (chartRef.current !== null) {
      const chart = getInstanceByDom(chartRef.current)
      chart?.setOption(option, settings)
    }
  }, [option, settings, theme]) // Whenever theme changes we need to add option and setting due to it being deleted in cleanup function

  useEffect(() => {
    // Update chart
    if (chartRef.current !== null) {
      const chart = getInstanceByDom(chartRef.current)
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      loading === true
        ? chart?.showLoading({
            text: '',
            showSpinner: false,
          })
        : chart?.hideLoading()
    }
  }, [loading, theme])

  return <div className={'font-Spartan w-full h-full'} ref={chartRef} />
}
